import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  X, Upload, Link, ChevronRight, ChevronLeft,
  CheckCircle, AlertCircle, Loader2, FileSpreadsheet,
  RefreshCw, Download, Sparkles, Lock
} from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../apiConfig';

/**
 * DataImportModal — admin-only 4-step wizard for importing leads
 * from CSV / Excel files or a Google Sheets URL.
 *
 * Steps:
 *   1. Source    — pick file or paste Sheets URL
 *   2. Map       — map spreadsheet columns → NorthAI lead fields
 *   3. Options   — skip duplicates / geocode
 *   4. Result    — progress + success/error summary
 */

const FIELDS_BY_TYPE = {
  lead: [
    { value: 'skip',              label: '— Skip this column —' },
    { value: 'name',              label: 'Name *' },
    { value: 'address',           label: 'Address' },
    { value: 'city',              label: 'City' },
    { value: 'prefecture',        label: 'Prefecture / State' },
    { value: 'postal_code',       label: 'Postal Code' },
    { value: 'phone',             label: 'Phone' },
    { value: 'email',             label: 'Email' },
    { value: 'website',           label: 'Website' },
    { value: 'contact_person',    label: 'Contact Person' },
    { value: 'latitude',          label: 'Latitude' },
    { value: 'longitude',         label: 'Longitude' },
    { value: 'type',              label: 'Business Type' },
    { value: 'industry',          label: 'Industry' },
    { value: 'priority',          label: 'Priority (high/medium/low)' },
    { value: 'forecasted_sales',  label: 'Forecasted Sales' },
    { value: 'opportunity_score', label: 'Opportunity Score (0–100)' },
    { value: 'notes',             label: 'Notes' },
  ],
  customer: [
    { value: 'skip',                      label: '— Skip this column —' },
    { value: 'name',                      label: 'Business Name *' },
    { value: 'address',                   label: 'Address' },
    { value: 'city',                      label: 'City' },
    { value: 'prefecture',                label: 'Prefecture / State' },
    { value: 'phone',                     label: 'Phone' },
    { value: 'email',                     label: 'Email' },
    { value: 'website',                   label: 'Website' },
    { value: 'contact_person',            label: 'Contact Person' },
    { value: 'latitude',                  label: 'Latitude' },
    { value: 'longitude',                 label: 'Longitude' },
    { value: 'type',                      label: 'Business Type' },
    { value: 'industry',                  label: 'Industry' },
    { value: 'status',                    label: 'Status (active/inactive/at_risk/churned)' },
    { value: 'tier',                      label: 'Tier (A / B / C)' },
    { value: 'actual_sales',              label: 'Annual Revenue / Sales' },
    { value: 'monthly_recurring_revenue', label: 'Monthly Recurring Revenue (MRR)' },
    { value: 'last_visit_date',           label: 'Last Visit Date' },
    { value: 'last_order_date',           label: 'Last Order Date' },
    { value: 'visit_frequency',           label: 'Visit Frequency (per month)' },
    { value: 'external_id',              label: 'External / CRM ID' },
    { value: 'notes',                     label: 'Notes' },
  ],
  transaction: [
    { value: 'skip',                 label: '— Skip this column —' },
    { value: 'customer_name',        label: 'Customer Name * (matched to existing customer)' },
    { value: 'customer_external_id', label: 'Customer External ID (alternative match key)' },
    { value: 'sales_date',           label: 'Sale Date *' },
    { value: 'amount',               label: 'Amount *' },
    { value: 'product_name',         label: 'Product Name' },
    { value: 'product_code',         label: 'Product Code / SKU' },
    { value: 'quantity',             label: 'Quantity' },
    { value: 'unit_price',           label: 'Unit Price' },
    { value: 'category',             label: 'Category (Inventory / Subscription / One-off)' },
    { value: 'notes',                label: 'Notes' },
    { value: 'external_id',          label: 'Order / Transaction ID' },
  ],
};

const ENTITY_TYPES = [
  {
    id: 'lead',
    label: 'Prospects',
    sublabel: 'Leads & targets',
    emoji: '🎯',
    desc: 'New businesses you want to visit and convert.',
    requiredFields: ['name'],
  },
  {
    id: 'customer',
    label: 'Customers',
    sublabel: 'Existing clients',
    emoji: '💼',
    desc: 'Your current accounts. Enables churn risk, visit tracking, and revenue intelligence.',
    requiredFields: ['name'],
  },
  {
    id: 'transaction',
    label: 'Sales History',
    sublabel: 'Revenue records',
    emoji: '💰',
    desc: 'Past sales per customer. Requires customers to be imported first.',
    requiredFields: ['customer_name', 'sales_date', 'amount'],
  },
];

const STEP_LABELS = ['Source', 'Map Columns', 'Options', 'Import'];

export default function DataImportModal({ onClose, onImportComplete }) {
  const [step, setStep] = useState(1);
  const [sourceTab, setSourceTab] = useState('file');   // 'file' | 'sheets'
  const [entityType, setEntityType] = useState('lead'); // 'lead' | 'customer' | 'transaction'

  // Step 1 state
  const [dragOver, setDragOver] = useState(false);
  const [sheetsUrl, setSheetsUrl] = useState('');
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Cancel any in-flight poll when the modal unmounts
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  // Step 2 state (preview data)
  const [tempKey, setTempKey] = useState('');
  const [columns, setColumns] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [columnMapping, setColumnMapping] = useState({}); // {srcCol: targetField}
  const [sourceType, setSourceType] = useState('file');
  const [uploadedFilename, setUploadedFilename] = useState('');

  // Step 3 state
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [geocodeMissing, setGeocodeMissing] = useState(true);

  // Lead Concierge (premium add-on) — entitlement comes from the preview
  // response; enrichment passes are off by default
  const [lcEnabled, setLcEnabled] = useState(false);
  const [enrichment, setEnrichment] = useState({
    unlimited_geocoding: false,
    fuzzy_dedup: false,
    ai_enrich: false,
  });

  // Step 4 state
  const [batchStatus, setBatchStatus] = useState(null); // null | 'processing' | 'completed' | 'failed'
  const [successRows, setSuccessRows] = useState(0);
  const [failedRows, setFailedRows] = useState(0);
  const [errorLog, setErrorLog] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ── Helpers ──────────────────────────────────────────────────────────────

  const currentEntityDef = ENTITY_TYPES.find(e => e.id === entityType) || ENTITY_TYPES[0];
  const importableFields = FIELDS_BY_TYPE[entityType] || FIELDS_BY_TYPE.lead;

  // True when all required fields for the chosen entity type are mapped
  const mappedValues = Object.values(columnMapping);
  const hasRequiredMapping = currentEntityDef.requiredFields.every(f => mappedValues.includes(f));

  // ── Step 1: upload / fetch preview ───────────────────────────────────────

  const handlePreviewResponse = useCallback((data) => {
    setTempKey(data.temp_key);
    setColumns(data.columns);
    setPreviewRows(data.preview_rows);
    setTotalRows(data.total_rows);
    setSourceType(data.source_type);
    setLcEnabled(Boolean(data.lead_concierge_enabled));
    // Initialise mapping from suggested + mark unrecognised as 'skip'
    const initial = {};
    data.columns.forEach(col => {
      initial[col] = data.suggested_mapping[col] || 'skip';
    });
    setColumnMapping(initial);
    setStep(2);
    setError('');
  }, []);

  const handleFileUpload = useCallback(async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) {
      setError('Unsupported file type. Please upload a .csv or .xlsx file.');
      return;
    }
    setLoading(true);
    setError('');
    setUploadedFilename(file.name);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', entityType);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/import/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      handlePreviewResponse(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to read file. Check the format and try again.');
    } finally {
      setLoading(false);
    }
  }, [entityType, handlePreviewResponse]);

  const handleSheetsPreview = useCallback(async () => {
    if (!sheetsUrl.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_BASE_URL}/api/import/google-sheets/preview`,
        { sheet_url: sheetsUrl.trim(), entity_type: entityType },
        { withCredentials: true }
      );
      handlePreviewResponse(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not fetch the sheet. Make sure it is shared or try re-connecting Google.');
    } finally {
      setLoading(false);
    }
  }, [sheetsUrl, entityType, handlePreviewResponse]);

  // Drag-and-drop
  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  }, [handleFileUpload]);

  // ── Step 4: poll until done ───────────────────────────────────────────────
  // Defined before handleConfirm so it can be listed in its deps array.

  const pollBatch = useCallback((id) => {
    let polls = 0;
    // ~10 minutes at 2s — AI enrichment + unlimited geocoding on large
    // files can legitimately run for several minutes
    const MAX_POLLS = 300;

    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);

    pollIntervalRef.current = setInterval(async () => {
      polls += 1;

      if (polls > MAX_POLLS) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setBatchStatus('failed');
        return;
      }

      try {
        const res = await axios.get(`${API_BASE_URL}/api/import/batches/${id}`, { withCredentials: true });
        const { status, successful_rows, failed_rows, error_log } = res.data;
        if (status !== 'processing') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setBatchStatus(status);
          setSuccessRows(successful_rows);
          setFailedRows(failed_rows);
          if (error_log) {
            try { setErrorLog(JSON.parse(error_log)); }
            catch { setErrorLog([error_log]); }
          }
          if (onImportComplete) onImportComplete();
        }
      } catch {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setBatchStatus('failed');
      }
    }, 2000);
  }, [onImportComplete]);

  // ── Step 3 → 4: trigger import ────────────────────────────────────────────

  const handleConfirm = useCallback(async () => {
    if (loading) return;
    if (!hasRequiredMapping) {
      const missing = currentEntityDef.requiredFields.join(', ');
      setError(`Please map the required field(s): ${missing}`);
      return;
    }
    setLoading(true);
    setError('');
    const mappingList = Object.entries(columnMapping)
      .filter(([, target]) => target && target !== 'skip')
      .map(([source_column, target_field]) => ({ source_column, target_field }));

    // Enrichment only applies to lead/customer imports, and only when the
    // org is entitled — the backend re-verifies regardless.
    const anyEnrichment = enrichment.unlimited_geocoding || enrichment.fuzzy_dedup || enrichment.ai_enrich;
    const sendEnrichment = lcEnabled && entityType !== 'transaction' && anyEnrichment;

    try {
      const res = await axios.post(`${API_BASE_URL}/api/import/confirm`, {
        temp_key: tempKey,
        column_mapping: mappingList,
        skip_duplicates: skipDuplicates,
        geocode_missing: geocodeMissing,
        source_type: sourceType,
        entity_type: entityType,
        filename: uploadedFilename || undefined,
        ...(sendEnrichment ? { enrichment } : {}),
      }, { withCredentials: true });

      setBatchStatus('processing');
      setStep(4);
      pollBatch(res.data.id);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'object' ? (detail.message || 'Failed to start import.') : (detail || 'Failed to start import.'));
    } finally {
      setLoading(false);
    }
  }, [loading, hasRequiredMapping, currentEntityDef, columnMapping, tempKey, skipDuplicates, geocodeMissing, sourceType, entityType, uploadedFilename, lcEnabled, enrichment, pollBatch]);

  // ── Error CSV download ────────────────────────────────────────────────────

  const downloadErrors = () => {
    const csv = ['Row,Error', ...errorLog.map(e => `"${String(e).replace(/"/g, '""')}"`)]
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'import_errors.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 z-[9000] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <FileSpreadsheet size={20} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Import Leads</h2>
              <p className="text-xs text-gray-500">Step {step} of 4 — {STEP_LABELS[step - 1]}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <X size={18} />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-1 px-6 pt-4 pb-2">
          {STEP_LABELS.map((label, i) => (
            <React.Fragment key={label}>
              <div className="flex items-center gap-1.5">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                  ${step > i + 1 ? 'bg-green-500 text-white' :
                    step === i + 1 ? 'bg-blue-600 text-white' :
                    'bg-gray-100 text-gray-400'}`}>
                  {step > i + 1 ? <CheckCircle size={14} /> : i + 1}
                </div>
                <span className={`text-xs hidden sm:inline ${step === i + 1 ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                  {label}
                </span>
              </div>
              {i < 3 && <div className={`flex-1 h-px ${step > i + 1 ? 'bg-green-400' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">

          {/* ── STEP 1: SOURCE ── */}
          {step === 1 && (
            <div className="space-y-4">

              {/* Entity type selector */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">What are you importing?</p>
                <div className="grid grid-cols-3 gap-2">
                  {ENTITY_TYPES.map(et => (
                    <button
                      key={et.id}
                      onClick={() => setEntityType(et.id)}
                      className={`flex flex-col items-start gap-1 p-3 rounded-xl border-2 text-left transition-colors
                        ${entityType === et.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                    >
                      <span className="text-xl">{et.emoji}</span>
                      <span className={`text-xs font-semibold ${entityType === et.id ? 'text-blue-700' : 'text-gray-800'}`}>
                        {et.label}
                      </span>
                      <span className="text-xs text-gray-400 leading-tight">{et.sublabel}</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">{currentEntityDef.desc}</p>
                {entityType === 'transaction' && (
                  <div className="flex items-start gap-2 mt-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                    Customers must be imported first — each transaction row is matched to an existing customer by name.
                  </div>
                )}
              </div>

              <div className="border-t border-gray-100" />

              {/* Source: file vs sheets */}
              <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => { setSourceTab('file'); setError(''); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-colors
                    ${sourceTab === 'file' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <Upload size={15} /> Upload File
                </button>
                <button
                  onClick={() => { setSourceTab('sheets'); setError(''); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-colors
                    ${sourceTab === 'sheets' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <Link size={15} /> Google Sheets
                </button>
              </div>

              {sourceTab === 'file' ? (
                <>
                  {/* Drop zone */}
                  <div
                    className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors
                      ${dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'}`}
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      className="hidden"
                      onChange={e => handleFileUpload(e.target.files[0])}
                    />
                    {loading ? (
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 size={32} className="text-blue-500 animate-spin" />
                        <span className="text-sm text-gray-500">Reading file…</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-3">
                        <div className="p-3 bg-gray-100 rounded-full">
                          <Upload size={24} className="text-gray-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Drop your file here or click to browse</p>
                          <p className="text-xs text-gray-400 mt-1">CSV or Excel (.xlsx) — max 10 MB, 5,000 rows</p>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Google Sheets URL</label>
                    <input
                      type="url"
                      value={sheetsUrl}
                      onChange={e => setSheetsUrl(e.target.value)}
                      placeholder="https://docs.google.com/spreadsheets/d/…"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Requires your Google account to be connected (Profile → Connect Google). The sheet must be accessible to your Google account.
                  </p>
                  <button
                    onClick={handleSheetsPreview}
                    disabled={!sheetsUrl.trim() || loading}
                    className="w-full py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg text-sm font-medium
                               disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <Link size={16} />}
                    {loading ? 'Fetching sheet…' : 'Fetch Sheet'}
                  </button>
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 2: COLUMN MAPPING ── */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Map your spreadsheet columns to NorthAI <span className="font-medium text-gray-900 capitalize">{entityType}</span> fields.
                  Required: {currentEntityDef.requiredFields.map(f => (
                    <strong key={f} className="text-gray-900"> {f.replace('_', ' ')}</strong>
                  ))}.
                </p>
                <span className="text-xs text-gray-400">{totalRows.toLocaleString()} rows</span>
              </div>

              {/* Mapping table */}
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-2.5 font-medium text-gray-600 w-1/2">Your column</th>
                      <th className="text-left px-4 py-2.5 font-medium text-gray-600 w-1/2">NorthAI field</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {columns.map(col => (
                      <tr key={col} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5 text-gray-700 font-medium truncate max-w-[200px]">{col}</td>
                        <td className="px-4 py-2.5">
                          <select
                            value={columnMapping[col] || 'skip'}
                            onChange={e => setColumnMapping(prev => ({ ...prev, [col]: e.target.value }))}
                            className={`w-full text-sm border rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500
                              ${columnMapping[col] === 'name' ? 'border-green-400 bg-green-50' :
                                columnMapping[col] && columnMapping[col] !== 'skip' ? 'border-blue-300 bg-blue-50' :
                                'border-gray-200 text-gray-400'}`}
                          >
                            {importableFields.map(f => (
                              <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Preview */}
              {previewRows.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">Preview (first 5 rows)</p>
                  <div className="overflow-x-auto border border-gray-200 rounded-xl">
                    <table className="text-xs w-full">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          {columns.map(col => (
                            <th key={col} className="text-left px-3 py-2 text-gray-500 font-medium whitespace-nowrap">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {previewRows.map((row, i) => (
                          <tr key={i}>
                            {columns.map(col => (
                              <td key={col} className="px-3 py-1.5 text-gray-600 whitespace-nowrap max-w-[120px] truncate">
                                {row[col] || ''}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {!hasRequiredMapping && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                  <AlertCircle size={16} className="flex-shrink-0" />
                  Required: map {currentEntityDef.requiredFields.map((f, i) => (
                    <span key={f}>{i > 0 ? ', ' : ''}<strong>{f.replace('_', ' ')}</strong></span>
                  ))} to continue.
                </div>
              )}
            </div>
          )}

          {/* ── STEP 3: OPTIONS ── */}
          {step === 3 && (
            <div className="space-y-5">
              <p className="text-sm text-gray-600">
                Ready to import <span className="font-semibold text-gray-900">{totalRows.toLocaleString()} rows</span>. Choose how to handle edge cases.
              </p>

              <label className="flex items-start gap-3 p-4 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  checked={skipDuplicates}
                  onChange={e => setSkipDuplicates(e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">Skip duplicate records</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Rows matching an existing lead's name + address in your org will be skipped.
                  </p>
                </div>
              </label>

              {entityType !== 'transaction' && (
                <label className="flex items-start gap-3 p-4 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={geocodeMissing}
                    onChange={e => setGeocodeMissing(e.target.checked)}
                    className="mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Geocode missing locations</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Rows without latitude/longitude will be geocoded from their address.
                      Limited to 1,000 rows per import. Requires Google Maps API key.
                    </p>
                  </div>
                </label>
              )}

              {/* ── Lead Concierge enrichment (premium add-on) ── */}
              {entityType !== 'transaction' && (
                lcEnabled ? (
                  <div className="border border-purple-200 rounded-xl overflow-hidden">
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-50 to-blue-50 border-b border-purple-100">
                      <Sparkles size={15} className="text-purple-600" />
                      <span className="text-sm font-semibold text-purple-800">Lead Concierge</span>
                      <span className="px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded text-[10px] font-bold uppercase tracking-wide">
                        Add-on active
                      </span>
                    </div>
                    <div className="p-2 space-y-1">
                      <label className="flex items-start gap-3 p-2.5 rounded-lg cursor-pointer hover:bg-purple-50/50 transition-colors">
                        <input
                          type="checkbox"
                          checked={enrichment.ai_enrich}
                          onChange={e => setEnrichment(prev => ({ ...prev, ai_enrich: e.target.checked }))}
                          className="mt-0.5 w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900">AI enrichment</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Fill missing industry labels and generate first-visit talking points for each business (up to 500 rows per import).
                          </p>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 p-2.5 rounded-lg cursor-pointer hover:bg-purple-50/50 transition-colors">
                        <input
                          type="checkbox"
                          checked={enrichment.fuzzy_dedup}
                          onChange={e => setEnrichment(prev => ({ ...prev, fuzzy_dedup: e.target.checked }))}
                          className="mt-0.5 w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Smart duplicate detection</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Catch near-duplicates like "Tanaka Bakery Inc." vs "tanaka bakery" — beyond exact name+address matching.
                          </p>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 p-2.5 rounded-lg cursor-pointer hover:bg-purple-50/50 transition-colors">
                        <input
                          type="checkbox"
                          checked={enrichment.unlimited_geocoding}
                          onChange={e => setEnrichment(prev => ({ ...prev, unlimited_geocoding: e.target.checked }))}
                          className="mt-0.5 w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Unlimited geocoding</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Lift the 1,000-row geocoding cap — every row missing coordinates gets geocoded.
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3 p-4 border border-gray-200 rounded-xl bg-gray-50">
                    <div className="p-1.5 bg-gray-200 rounded-lg flex-shrink-0">
                      <Lock size={14} className="text-gray-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                        <Sparkles size={13} className="text-purple-400" /> Lead Concierge enrichment
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        AI industry classification, talking points, smart dedup, and unlimited geocoding
                        are available with the Lead Concierge add-on. Enable it in <strong>Admin Hub → Services</strong>.
                      </p>
                    </div>
                  </div>
                )
              )}

              <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl text-sm text-blue-700">
                {entityType === 'lead' && (
                  <>Leads with coordinates are <strong>auto-assigned</strong> to the salesperson who owns that territory. Leads without coordinates import as <strong>unassigned</strong> and can be claimed from the map.</>
                )}
                {entityType === 'customer' && (
                  <>Customers will be imported with status <strong>Active</strong> (unless your data says otherwise). They appear on the map immediately.</>
                )}
                {entityType === 'transaction' && (
                  <>Transactions are matched to existing customers by name. Rows with no matching customer are skipped and reported in the error log.</>
                )}
              </div>
            </div>
          )}

          {/* ── STEP 4: PROGRESS + RESULT ── */}
          {step === 4 && (
            <div className="space-y-5 py-4">
              {batchStatus === 'processing' && (
                <div className="flex flex-col items-center gap-4 py-8">
                  <Loader2 size={40} className="text-blue-500 animate-spin" />
                  <div className="text-center">
                    <p className="text-base font-medium text-gray-900">
                      Importing your {entityType === 'transaction' ? 'sales history' : `${entityType}s`}…
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {enrichment.ai_enrich && lcEnabled
                        ? 'AI enrichment is running — this can take a few minutes for large files.'
                        : 'This usually takes under a minute.'}
                    </p>
                  </div>
                </div>
              )}

              {batchStatus === 'completed' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                    <CheckCircle size={24} className="text-green-500 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-green-800">Import complete</p>
                      <p className="text-sm text-green-700 mt-0.5">
                        <span className="font-bold">{successRows.toLocaleString()}</span> leads added to NorthAI
                        {failedRows > 0 && <span className="text-amber-600 ml-2">· {failedRows.toLocaleString()} skipped</span>}
                      </p>
                    </div>
                  </div>

                  {errorLog.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-gray-700">Skipped rows</p>
                        <button
                          onClick={downloadErrors}
                          className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700"
                        >
                          <Download size={13} /> Download CSV
                        </button>
                      </div>
                      <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg bg-gray-50 p-3 space-y-1">
                        {errorLog.slice(0, 50).map((e, i) => (
                          <p key={i} className="text-xs text-gray-600 font-mono">{e}</p>
                        ))}
                        {errorLog.length > 50 && (
                          <p className="text-xs text-gray-400 italic">… and {errorLog.length - 50} more. Download for full list.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {batchStatus === 'failed' && (
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <AlertCircle size={22} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-red-800">Import failed</p>
                    <p className="text-sm text-red-700 mt-1">
                      Something went wrong during processing. Check the file format and try again.
                    </p>
                    <button
                      onClick={() => { setStep(1); setBatchStatus(null); setError(''); }}
                      className="mt-3 flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      <RefreshCw size={14} /> Try again
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={() => step > 1 && step < 4 ? setStep(s => s - 1) : onClose}
            className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {step > 1 && step < 4 ? <><ChevronLeft size={16} /> Back</> : 'Close'}
          </button>

          {step < 3 && (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={(step === 1 && !tempKey) || (step === 2 && !hasRequiredMapping)}
              className="flex items-center gap-1.5 px-5 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg text-sm font-medium
                         disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
            >
              Continue <ChevronRight size={16} />
            </button>
          )}

          {step === 3 && (
            <button
              onClick={handleConfirm}
              disabled={loading}
              className="flex items-center gap-1.5 px-5 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg text-sm font-medium
                         disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              {loading ? 'Starting…' : `Import ${totalRows.toLocaleString()} rows`}
            </button>
          )}

          {step === 4 && batchStatus === 'completed' && (
            <button
              onClick={onClose}
              className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
